import React, { useState } from 'react';
import { DownloadAppModal, SharedHero } from '@hover/shared-components';
import { useMediaQuery } from '@chakra-ui/react';
import { navigate } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import { Box } from '@hover/blueprint';
import { useABTesting } from '../../../hooks';
import { ctaABTesting } from '../../../utils/ctaABTesting';
import { Layout, UtilityMenuBanner } from '../../common';
import { URLS } from '../../../constants';
import {
  INDEPENDENT_ADJUSTERS_PARTNERS,
  INDEPENDENT_ADJUSTERS_PROS,
  INDEPENDENT_ADJUSTERS_RESOURCES,
  INDEPENDENT_ADJUSTERS_TESTIMONIES,
  COMPLETE_JOBS_FASTER_POINTS,
  SINGLE_SOLUTION_POINTS,
  SUBMIT_CLAIMS_POINTS,
} from '../../../constants/independentAdjusters';
import { Stats, HoverPros, FAQ, PartnerList } from '../index';
import Resources from '../../common/solutions/resources';
import SolutionsTestimony from '../../common/solutions/testimony/Testimony';
import Points from '../../common/solutions/pointsList';
import { usePageViewTracking } from '../../../../utils/trackPageView';

const NormalVersion: React.FC = () => {
  usePageViewTracking('independent-adjusters-normal');

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [isDesktop] = useMediaQuery('(min-width: 1280px)');
  const abTestValue = useABTesting({ testName: 'measure-page-cta-pressed' });

  if (!abTestValue) return <>Loading...</>;

  const toggleModal = () => {
    if (!isDesktop) setIsOpen(!isOpen);
    else if (abTestValue === 'A') {
      ctaABTesting('A');
      setIsOpen(!isOpen);
    } else {
      ctaABTesting('B');
      void navigate('/signup');
    }
  };
  return (
    <Layout
      title="Independent Adjusters - Faster property inspections start here"
      description="HOVER is the only solution that eliminates the need to hand measure for both exteriors and interiors."
      buttonType="SCHEDULE_A_DEMO"
      bannerElement={<UtilityMenuBanner />}
    >
      <DownloadAppModal isOpen={isOpen} onClose={toggleModal} />
      <SharedHero
        page="independent-adjusters"
        bgColor="primary.100"
        heading="Faster property inspections start here"
        body="HOVER is the only solution that eliminates the need to hand measure for both exteriors and interiors."
        openModalButtonTitle="GET STARTED"
        openModalButtonLink={URLS.independentAdjusters.tryItNow}
        chatWithUsLinkTitle="Chat with us"
        py={8}
        chatWithUsLink={URLS.independentAdjusters.chatWithARep}
        desktopImage={
          <StaticImage
            loading="eager"
            placeholder="blurred"
            src="../../../assets/images/independent_adjusters_hero.png"
            alt=""
            style={{ maxWidth: '536px' }}
          />
        }
        mobileAndTabletImage={
          <StaticImage
            loading="eager"
            placeholder="blurred"
            src="../../../assets/images/independent_adjusters_hero.png"
            alt=""
          />
        }
        justifyItems="center"
        mb={{ base: 900, tablet: 900 }}
      />
      <Stats />
      <HoverPros
        title="A single solution for every claim"
        items={INDEPENDENT_ADJUSTERS_PROS}
        d={{ base: 'none', tablet: 'flex' }}
      />
      <Points
        items={SUBMIT_CLAIMS_POINTS}
        heading="Submit claims with confidence"
        url={() => toggleModal()}
        urlTitle="GET STARTED"
        image={
          <StaticImage
            src="../../../assets/images/independent_adjusters_points_1.png"
            alt=""
          />
        }
        isReversed
        pt={{ base: 1000, desktop: 900 }}
        pb={{ base: 800, desktop: 700 }}
        pointsGap="24px"
        pointsGapMobile="20px"
        direction={{ base: 'column-reverse', tablet: 'row' }}
      />
      <Points
        items={COMPLETE_JOBS_FASTER_POINTS}
        heading="Complete jobs faster"
        url={() => toggleModal()}
        urlTitle="GET STARTED"
        image={
          <Box maxW="550px">
            <StaticImage
              src="../../../assets/images/independent_adjusters_points_2.png"
              alt=""
            />
          </Box>
        }
        pt={{ base: 800, desktop: 700 }}
        pb={{ base: 700, desktop: 700 }}
        pointsGap="24px"
        pointsGapMobile="20px"
        gap="3rem"
        direction={{ base: 'column-reverse', tablet: 'row' }}
      />

      <Points
        items={SINGLE_SOLUTION_POINTS}
        heading="Your single solution for every claim"
        url={() => toggleModal()}
        urlTitle="GET STARTED"
        image={
          <StaticImage
            src="../../../assets/images/independent_adjusters_points_3.png"
            alt=""
          />
        }
        isReversed
        py="92px"
        pointsGap="24px"
        pointsGapMobile="20px"
        direction={{ base: 'column-reverse', tablet: 'row' }}
      />
      <HoverPros
        title="Single solution multiple benefits"
        items={INDEPENDENT_ADJUSTERS_PROS}
        d={{ base: 'flex', tablet: 'none' }}
      />
      <PartnerList
        partners={INDEPENDENT_ADJUSTERS_PARTNERS}
        heading="We work with the same leading companies that you do"
        desktopRows={4}
      />
      <SolutionsTestimony items={INDEPENDENT_ADJUSTERS_TESTIMONIES} />
      <Resources
        items={INDEPENDENT_ADJUSTERS_RESOURCES}
        columns={{ base: 1, tablet: 3, desktop: 3 }}
        maxWidth="1020px"
        mb={{ base: '56px', tablet: 900, desktop: 0 }}
      />
      <FAQ />
    </Layout>
  );
};

export default NormalVersion;
